import { AsyncResult } from './async-result';

const noValue = Symbol('noValue');
type NoValue = typeof noValue;

type AnyFunction = (...args: any) => any;

export function memoizeByRule<TFunc extends AnyFunction>(
  f: TFunc,
  shouldReuseLastResult: (
    args: Parameters<TFunc>,
    lastArgs: Parameters<TFunc>,
    result: ReturnType<TFunc>,
    lastResult: ReturnType<TFunc>,
  ) => boolean,
): (...args: Parameters<TFunc>) => ReturnType<TFunc> {
  let last: { args: Parameters<TFunc>; result: ReturnType<TFunc> } | NoValue =
    noValue;
  return (...args: Parameters<TFunc>): ReturnType<TFunc> => {
    const result = f(...args);
    if (
      last !== noValue &&
      shouldReuseLastResult(args, last.args, result, last.result)
    ) {
      return last.result;
    }
    last = { args, result };
    return result;
  };
}

type AsyncResultFunction<TResult> = (...args: any) => AsyncResult<TResult>;
type InnerTypeOfAsyncResult<TFunc> = TFunc extends AsyncResultFunction<
  infer TInner
>
  ? TInner
  : never;

export function memoizeAsyncResult<TFunc extends AsyncResultFunction<any>>(
  f: TFunc,
  shouldReuseLastInnerResult: (
    argsInner: Parameters<TFunc>,
    lastArgsInner: Parameters<TFunc>,
    resultInner: InnerTypeOfAsyncResult<TFunc>,
    lastResultInner: InnerTypeOfAsyncResult<TFunc>,
  ) => boolean,
): (...args: Parameters<TFunc>) => ReturnType<TFunc> {
  return memoizeByRule(
    f,
    (args, lastArgs, result, lastResult) =>
      (result.isUninitialized && lastResult.isUninitialized) ||
      (result.isLoading && lastResult.isLoading) ||
      (result.isError && lastResult.isError) ||
      (result.isSuccess &&
        lastResult.isSuccess &&
        shouldReuseLastInnerResult(
          args,
          lastArgs,
          result.data,
          lastResult.data,
        )),
  );
}
